import type { storeTag as ruStoreTag } from '../ru/storeTag';

export const storeTag: typeof ruStoreTag = {
  editor: {
    nameLabel: 'Tag text',
    namePlaceholder: 'For example, RATTEN',
    nameHint: 'Letters, digits, spaces and the characters _ . - !',
    counter: (length: number, max: number): string => `${length} / ${max}`,
    nameErrors: {
      length: 'The text must be 2 to 20 characters long.',
      chars: 'Only letters, digits, spaces and the characters _ . - ! are allowed.',
      start: 'The tag must start with a letter or a digit.',
      spaces: 'Remove double spaces.'
    },
    presetsTitle: 'Ready-made styles',
    presetAria: (name: string): string => `Style "${name}"`,
    presets: {
      gold: 'Gold',
      ember: 'Amber',
      rose: 'Rose',
      mint: 'Mint',
      sky: 'Sky',
      violet: 'Violet',
      sunset: 'Sunset',
      aurora: 'Aurora',
      toxic: 'Toxic',
      ocean: 'Ocean',
      flame: 'Flame',
      candy: 'Candy'
    },
    customTitle: 'Custom color',
    customHint: 'One color is solid, two to six make a gradient.',
    colorAria: (index: number): string => `Color ${index}`,
    hexAria: 'Color code',
    paletteAria: 'Palette',
    colorDone: 'Done',
    addColor: '+ color',
    removeColorAria: (index: number): string => `Remove color ${index}`,
    angleTitle: 'Gradient direction',
    angleAria: (degrees: number): string => `Direction ${degrees}°`,
    iconTitle: 'Icon',
    iconOptional: 'optional',
    iconUpload: 'Upload icon',
    iconReplace: 'Replace',
    iconRemove: 'Remove icon',
    iconProcessing: 'Processing…',
    iconHint: 'PNG, JPEG or WebP up to 5 MB. We crop a centered square and shrink it to 64×64.',
    iconPreviewAlt: 'Tag icon',
    iconErrors: {
      type: 'Only PNG, JPEG and WebP images are supported.',
      size: 'The file is larger than 5 MB. Pick a smaller image.',
      decode: 'Could not read the image. Try another file.'
    },
    previewPlaceholderName: 'Tag'
  },
  page: {
    kicker: 'Custom tag',
    title: 'Create your own tag',
    subtitle: 'The tag is shown next to your name in matches. One purchase, yours forever; a moderator reviews it before it is issued.',
    stepsAria: 'Checkout steps',
    steps: {
      style: 'Design',
      confirm: 'Confirmation'
    },
    loginTitle: 'Sign in to create a tag',
    loginDescription: 'The tag is linked to your game account, so a Steam sign-in is required. After signing in you will come back to this page.',
    loading: 'Loading the product…',
    unavailableTitle: 'Tag purchase is not available yet',
    unavailableText: 'Tag sales are not open yet. Please check back later.',
    loadFailed: 'Could not load the product',
    backToStore: 'Back to the store',
    previewTitle: 'How the tag looks in game',
    previewHint: 'The player card in a match. Long text shrinks automatically in game.',
    contactLabel: 'How can we reach you if something is wrong with the tag',
    contactPlaceholder: '@nickname on Discord, Telegram, or a Steam link',
    contactHint: 'A moderator will message you if the tag needs a fix. Give a contact you actually read: Discord, Telegram or Steam.',
    contactError: 'The contact must be 2 to 500 characters long.',
    next: 'Next',
    back: 'Back'
  },
  confirm: {
    title: 'Review your order',
    previewTitle: 'Your tag',
    summaryName: 'Tag text',
    summaryContact: 'Contact',
    summaryTerm: 'Term',
    summaryPrice: 'Price',
    summaryForever: 'Forever',
    payTitle: 'Payment',
    submit: 'Pay',
    editStyle: 'Edit the design',
    mustFixStyle: 'Go back to the "Design" step and fix the text, colors or icon.'
  },
  card: {
    cta: 'Create your tag',
    ctaAria: (name: string): string => `Create your tag: ${name}`
  },
  status: {
    'awaiting-payment': 'Awaiting payment',
    'pending-moderation': 'Under review',
    approved: 'Approved',
    rejected: 'Rejected'
  },
  order: {
    title: 'Your tag',
    contactLabel: 'Contact',
    moderatorComment: 'Moderator comment',
    awaitingPayment: 'The tag goes to moderation once the order is paid.',
    pendingModerationTitle: 'Payment received, the tag is under review',
    pendingModerationText: 'A moderator will review the tag and may contact you using the contact you gave. Once approved, the tag appears in your game.',
    approvedTitle: 'Tag approved: it is already yours',
    approvedText: 'The tag is linked to your game account and will be visible in game.',
    rejectedTitle: 'Tag rejected',
    rejectedText: 'The money for the order will be refunded manually. If you have questions, write to support and quote the order number.',
    rowTag: (name: string): string => `Tag "${name}"`
  },
  admin: {
    status: {
      all: 'All',
      'pending-moderation': 'Under review',
      approved: 'Approved',
      rejected: 'Rejected'
    },
    filterStatus: 'Status',
    loadFailed: 'Could not load tag requests',
    empty: 'No tag requests',
    tableAria: 'Tag requests',
    player: 'Player',
    playerUid: (uid: string): string => `UID ${uid}`,
    order: 'Order',
    contact: 'Contact',
    created: 'Created',
    paid: 'Paid',
    tagCode: 'Tag code',
    moderated: (name: string, date: string): string => `Reviewed by ${name}, ${date}`,
    moderatedNoName: (date: string): string => `Reviewed: ${date}`,
    comment: 'Moderator comment',
    edit: 'Edit',
    approve: 'Approve',
    reject: 'Reject',
    editAria: (name: string): string => `Edit tag ${name}`,
    approveAria: (name: string): string => `Approve tag ${name}`,
    rejectAria: (name: string): string => `Reject tag ${name}`,
    editDialog: {
      title: 'Edit tag',
      save: 'Save',
      saving: 'Saving…',
      invalid: 'Check the tag text and colors.'
    },
    approveDialog: {
      title: 'Approve the tag?',
      description: 'The tag will be assigned to the player and replace their current tag.',
      confirm: 'Approve'
    },
    rejectDialog: {
      title: 'Reject the tag',
      description: 'The tag will be deleted and the player will see the reason on the order page. The money must be refunded manually.',
      commentLabel: 'Reason for rejection',
      commentPlaceholder: 'For example, the text violates the community rules',
      commentRequired: 'Enter a reason for rejection.',
      commentTooLong: 'The reason is too long (500 characters max).',
      confirm: 'Reject',
      back: 'Back'
    },
    toasts: {
      updated: 'Tag updated',
      approved: 'Tag approved and assigned to the player',
      rejected: 'Request rejected'
    }
  }
};
